import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Contexts
import { AuthProvider } from './context/AuthContext';
import { StoreProvider } from './context/StoreContext';

// Layout & Global Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/AccessControl/ProtectedRoute';
import AccessSync from './components/AccessControl/AccessSync';

// Auth Pages
import Login from './pages/Auth/Login/Login';

// Core Dashboard & Modules
import DashboardEnterprise from './pages/DashboardEnterprise/DashboardEnterprise';
import CreateBill from './pages/CreateBill/CreateBill';
import InvoicePreview from './pages/InvoicePreview/InvoicePreview';
import InvoiceHistory from './pages/InvoiceHistory/InvoiceHistory';
import PublicInvoice from './pages/InvoicePreview/components/PublicInvoice/PublicInvoice';
import PublicDailyReport from './pages/PublicDailyReport/PublicDailyReport';
import PublicFeedback from './pages/PublicFeedback/PublicFeedback';
import DailyReport from './pages/DailyReport/DailyReport';
import CashFlow from './pages/CashFlow/CashFlow';
import Vendors from './pages/Vendors/Vendors';
import VendorDetail from './pages/Vendors/VendorDetail';

import ExpenseManagement from './pages/ExpenseManagement/ExpenseManagement';
import AppointmentBooking from './pages/AppointmentBooking/AppointmentBooking';
import FeedbackManagement from './pages/FeedbackManagement/FeedbackManagement';
import Royalty from './pages/Royalty/Royalty';
import WarehousePage from './pages/Warehouse/Warehouse';
import NetworkStatus from './components/NetworkStatus/NetworkStatus';
import Analytics from './pages/Analytics/Analytics';
import CardAllotment from './components/CardAllotment/CardAllotment';

// Pricelist & Services
import PricelistOverview from './pages/Pricelist/components/PricelistOverview/PricelistOverview';
import PricelistMaster from './pages/Pricelist/components/PricelistMaster/PricelistMaster';
import ServiceMaster from './pages/Pricelist/components/ServiceMaster/ServiceMaster';
import PricelistAddons from './pages/Pricelist/PricelistAddons/PricelistAddons';
import Pricelist from './pages/Pricelist/Pricelist';

// Loyalty Programs
import LoyaltyPrograms from './pages/Manage/LoyaltyPrograms/LoyaltyPrograms';
import RenewLoyalty from './pages/Manage/LoyaltyPrograms/components/RenewLoyalty/RenewLoyalty';

// Inventory Management
import InventoryPage from './pages/Inventory/InventoryPage/InventoryPage';
import StockOverview from './pages/Inventory/StockOverview/StockOverview';
import AddProduct from './pages/Inventory/AddProduct/AddProduct';
import StockUpdate from './pages/Inventory/StockUpdate/StockUpdate';
import InventoryAnalytics from './pages/Analytics/InventoryAnalytics/InventoryAnalytics';

// User & Staff Management
import UserManagement from './pages/UserManagement/UserManagement';
import ManageAccess from './pages/UserManagement/ManageAccess/ManageAccess';
import UserCredentials from './pages/UserManagement/UserCredentials/UserCredentials';
import ManageStaff from './pages/ManageStaff/ManageStaff';
import StaffProfile from './pages/ManageStaff/StaffProfile/StaffProfile';
import Attendance from './pages/Attendance/Attendance';
import Payroll from './pages/Payroll/Payroll';

// Settings & Access Control
import Settings from './pages/Settings/Settings';
import AccessControl from './pages/Settings/AccessControl/AccessControl';
import GlobalAccessMatrix from './pages/GlobalAccessMatrix/GlobalAccessMatrix';
import StoreActivator from './components/StoreActivator/StoreActivator';

// Clients & Bookings
import Clients from './pages/Clients/Clients';
import ClientProfile from './pages/Clients/ClientProfile/ClientProfile';
import BookingHistory from './pages/BookingHistory/BookingHistory';
import BookingDetails from './pages/BookingHistory/BookingDetails/BookingDetails';

// Integrations
import IntegrationsLayout from './pages/Integrations/IntegrationsLayout/IntegrationsLayout';
import WhatsAppIntegration from './pages/Integrations/WhatsAppIntegration/WhatsAppIntegration';

// Config
import ConfigLayout from './pages/Config/ConfigLayout/ConfigLayout';
import EodConfiguration from './pages/Config/EodConfiguration/EodConfiguration';
import WalletBilling from './pages/WalletBilling/WalletBilling';

// Marketing
import MarketingLayout from './pages/Marketing/MarketingLayout';
import WhatsAppDashboard from './pages/Marketing/components/WhatsAppDashboard/WhatsAppDashboard';
import Campaigns from './pages/Marketing/components/Campaigns/Campaigns';
import Templates from './pages/Marketing/components/Templates/Templates';
import Audience from './pages/Marketing/components/Audience/Audience';
import { MarketingSettings } from './pages/Marketing/components/MarketingSettings/MarketingSettings';
import CreateTemplate from './pages/Marketing/components/Templates/CreateTemplate/CreateTemplate';
import CreateCampaign from './pages/Marketing/components/Campaigns/CreateCampaign/CreateCampaign';
import CampaignAnalytics from './pages/Marketing/components/Campaigns/CampaignAnalytics/CampaignAnalytics';
import HirstaAccess from './pages/HirstaAccess/HirstaAccess';
import WhatsAppMessages from './pages/Settings/WhatsAppMessages/WhatsAppMessages';

// Placeholder Components
const SmsIntegration = () => <div style={{ padding: '30px' }}><h2>SMS Gateway</h2><p>Coming soon...</p></div>;
const EmailIntegration = () => <div style={{ padding: '30px' }}><h2>Email SMTP</h2><p>Coming soon...</p></div>;
const WalletDashboard = () => <div style={{ padding: '30px' }}><h2>Store Wallet & Billing</h2><p>Initializing...</p></div>;

const Unauthorized = () => (
  <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-family-base, Nunito, sans-serif)' }}>
    <h1 style={{ color: 'var(--h-color-danger-600, #be1025)', fontSize: '3rem', margin: 0 }}>403</h1>
    <h2>Access Denied</h2>
    <p>You do not have permission to view this page.</p>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      {/* internet gone / server not answering / back online (on every page, public ones too) */}
      <NetworkStatus />
      <AuthProvider>
        <StoreProvider>
          {/* Loads live access (own policy limited by the HQ branch limit) on login and branch change */}
          <AccessSync />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Public (no login): customer opens the bill from the WhatsApp / SMS link */}
            <Route path="/i/:token" element={<PublicInvoice />} />
            <Route path="/bill/:token" element={<PublicInvoice />} />
            <Route path="/r/:token" element={<PublicDailyReport />} />
            <Route path="/f/:token" element={<PublicFeedback />} />
            <Route path="/hirsta-access" element={<HirstaAccess />} />

            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              {/* Dashboard */}
              <Route index element={<ProtectedRoute module="dashboard" action="view"><DashboardEnterprise /></ProtectedRoute>} />

              {/* Billing & bookings */}
              <Route path="booking-history" element={<ProtectedRoute any={['appointments.view_booking_history', 'billing.view_history']}><BookingHistory /></ProtectedRoute>} />
              <Route path="booking/:id" element={<ProtectedRoute any={['appointments.view_booking_history', 'billing.view_bill']}><BookingDetails /></ProtectedRoute>} />
              <Route path="appointments" element={<ProtectedRoute module="appointments" action="view"><AppointmentBooking /></ProtectedRoute>} />
              <Route path="reports/daily" element={<ProtectedRoute any={['reports.view_daily_report', 'reports.view_eod_report']}><DailyReport /></ProtectedRoute>} />
              <Route path="cash-flow" element={<ProtectedRoute module="cashflow" action="view"><CashFlow /></ProtectedRoute>} />

              <Route path="invoices" element={<ProtectedRoute module="billing" action="view_history"><InvoiceHistory /></ProtectedRoute>} />
              <Route path="create-bill" element={<ProtectedRoute module="billing" action="create"><CreateBill /></ProtectedRoute>} />
              <Route path="create-bill/:id" element={<ProtectedRoute module="billing" action="create"><CreateBill /></ProtectedRoute>} />
              <Route path="update-invoice/:id" element={<ProtectedRoute module="billing" action="edit"><CreateBill /></ProtectedRoute>} />
              <Route path="create-bill/:id/preview" element={<ProtectedRoute module="billing" action="view_bill"><InvoicePreview /></ProtectedRoute>} />

              {/* Pricelist */}
              <Route path="pricelist/overview" element={<ProtectedRoute module="pricelist" action="view"><Pricelist /></ProtectedRoute>} />
              <Route path="pricelist" element={<ProtectedRoute module="pricelist" action="view"><PricelistOverview /></ProtectedRoute>} />
              <Route path="pricelist/master" element={<ProtectedRoute module="pricelist" action="view"><PricelistMaster /></ProtectedRoute>} />
              <Route path="pricelist/service/add" element={<ProtectedRoute module="pricelist" action="view"><ServiceMaster /></ProtectedRoute>} />
              <Route path="pricelist/add" element={<ProtectedRoute module="pricelist" action="view"><PricelistAddons /></ProtectedRoute>} />

              {/* Staff & attendance ("new" must stay above ":id") */}
              <Route path="staff" element={<ProtectedRoute module="staff" action="view"><ManageStaff /></ProtectedRoute>} />
              <Route path="staff/new" element={<ProtectedRoute module="staff" action="create"><StaffProfile /></ProtectedRoute>} />
              <Route path="staff/:id" element={<ProtectedRoute module="staff" action="view"><StaffProfile /></ProtectedRoute>} />
              <Route path="attendance" element={<ProtectedRoute module="attendance" action="view"><Attendance /></ProtectedRoute>} />
              <Route path="payroll" element={<ProtectedRoute module="payroll" action="view"><Payroll /></ProtectedRoute>} />

              {/* Users */}
              <Route path="users" element={<ProtectedRoute module="users" action="view"><UserManagement /></ProtectedRoute>} />
              <Route path="users/:userId/credentials" element={<ProtectedRoute module="users" action="edit"><UserCredentials /></ProtectedRoute>} />
              <Route path="users/:userId/manage-access" element={<ProtectedRoute module="users" action="edit"><ManageAccess /></ProtectedRoute>} />

              {/* Inventory */}
              <Route path="inventory" element={<ProtectedRoute module="inventory" action="view"><InventoryPage /></ProtectedRoute>} />
              <Route path="inventory/overview" element={<ProtectedRoute module="inventory" action="view"><StockOverview /></ProtectedRoute>} />
              <Route path="inventory/add" element={<ProtectedRoute module="inventory" action="create"><AddProduct /></ProtectedRoute>} />
              <Route path="inventory/update" element={<ProtectedRoute module="inventory" action="view"><StockUpdate /></ProtectedRoute>} />
              <Route path="inventory/analytics" element={<ProtectedRoute module="inventory" action="view"><InventoryAnalytics /></ProtectedRoute>} />
              <Route path="inventory/vendors" element={<ProtectedRoute module="vendors" action="view"><Vendors /></ProtectedRoute>} />
              <Route path="inventory/vendors/:id" element={<ProtectedRoute module="vendors" action="view"><VendorDetail /></ProtectedRoute>} />
              <Route path="vendors/*" element={<Navigate to="/inventory/vendors" replace />} />

              {/* Business */}
              <Route path="expenses" element={<ProtectedRoute module="expense" action="view"><ExpenseManagement /></ProtectedRoute>} />
              <Route path="feedback" element={<ProtectedRoute module="feedback" action="view"><FeedbackManagement /></ProtectedRoute>} />
              {/* Franchise royalty (HQ): agreements, monthly statements, payments, projection */}
              <Route path="royalty" element={<Royalty />} />
              {/* Warehouse: HQ keeps stock and sends it to branches (with bills); a branch receives its stock here */}
              <Route path="warehouse" element={<WarehousePage />} />
              <Route path="analytics/:reportId?" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />

              {/* Loyalty */}
              <Route path="manage-loyalty-program/*" element={<ProtectedRoute><LoyaltyPrograms /></ProtectedRoute>} />
              <Route path="renew-loyalty" element={<ProtectedRoute><RenewLoyalty onBack={() => window.history.back()} /></ProtectedRoute>} />
              <Route path="card-allotment" element={<ProtectedRoute><CardAllotment /></ProtectedRoute>} />

              {/* Settings & access */}
              <Route path="settings" element={<ProtectedRoute module="settings" action="view"><Settings /></ProtectedRoute>} />
              <Route path="access-control" element={<ProtectedRoute module="settings" action="view"><AccessControl /></ProtectedRoute>} />
              <Route path="global-access" element={<ProtectedRoute module="settings" action="manage_global"><GlobalAccessMatrix /></ProtectedRoute>} />
              <Route path="dev/activate-stores" element={<ProtectedRoute module="settings" action="manage_global"><StoreActivator /></ProtectedRoute>} />
              <Route path="wallet-billing" element={<ProtectedRoute module="wallet" action="view"><WalletBilling /></ProtectedRoute>} />

              {/* Clients */}
              <Route path="clients" element={<ProtectedRoute module="customers" action="view"><Clients /></ProtectedRoute>} />
              <Route path="clients/:id" element={<ProtectedRoute module="customers" action="view"><ClientProfile /></ProtectedRoute>} />

              {/* Integrations */}
              <Route path="integrations" element={<ProtectedRoute module="integrations" action="view"><IntegrationsLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="whatsapp" replace />} />
                <Route path="whatsapp" element={<WhatsAppIntegration />} />
                {/* Which WhatsApp template goes out for each moment: day closing, bill, feedback,
                    appointments (client + staff), membership, package, balance */}
                <Route path="whatsapp-messages" element={<WhatsAppMessages />} />
                <Route path="sms" element={<SmsIntegration />} />
                <Route path="email" element={<EmailIntegration />} />
              </Route>

              {/* Config */}
              <Route path="config" element={<ProtectedRoute module="settings" action="view"><ConfigLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="eod" replace />} />
                <Route path="eod" element={<EodConfiguration />} />
                <Route path="wallet" element={<WalletDashboard />} />
              </Route>

              {/* Marketing */}
              <Route path="marketing" element={<ProtectedRoute module="marketing" action="view"><MarketingLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="whatsapp" replace />} />
                <Route path="whatsapp" element={<WhatsAppDashboard />} />
                <Route path="campaigns">
                  <Route index element={<Campaigns />} />
                  <Route path="create" element={<CreateCampaign />} />
                  <Route path=":id" element={<CampaignAnalytics />} />
                </Route>
                <Route path="templates">
                  <Route index element={<Templates />} />
                  <Route path="create" element={<CreateTemplate />} />
                  <Route path="edit/:id" element={<CreateTemplate />} />
                </Route>
                <Route path="audience" element={<Audience />} />
                <Route path="settings" element={<MarketingSettings />} />
              </Route>

              {/* Unknown page inside the app -> dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;